/** @jsxImportSource @emotion/react */

'use client';
'use no memo';

import { ChevronLeft, ChevronRight, Close } from '@mui/icons-material';
import {
  Box,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  type SxProps,
  Typography,
} from '@mui/material';
import {
  type Column,
  type ColumnDef,
  type ColumnOrderState,
  type ColumnSizingState,
  type ColumnSort,
  type ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  type Header,
  type HeaderContext,
  type OnChangeFn,
  type Table as ReactTable,
  type Row,
  type RowSelectionState,
  type Updater,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table';
import {
  type MutableRefObject,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { pluralize, upcaseFirst } from '@campaign-manager/core/lib/utils';
import { useCompactView } from '@campaign-manager/feature/hook/useCompactView';
import { useTableSettings } from '@campaign-manager/feature/hook/useTableSettings';
import { Button, Checkbox } from '@campaign-manager/react/components';

import {
  TableBody as EnhancedTableBody,
  TableHead as EnhancedTableHead,
  TableComponent,
  TableWrapper,
} from '../Table';
import Text from '../Text';

interface Props<T> {
  tableId: string; // Unique identifier for persisting settings
  columns: ColumnDef<T>[];
  data: T[];
  loading?: boolean;
  error?: Error;
  errorState?: ReactNode;
  emptyState?: ReactNode;
  getRowCanExpand?: (row: Row<T>) => boolean;
  expandedRowRender?: (row: Row<T>) => ReactNode;
  defaultExpanded?: boolean;
  expandedRowSpansFull?: boolean;
  afterEachRowRender?: (row: Row<T>) => ReactNode;
  showSorting?: boolean;
  manualSorting?: boolean;
  defaultOrderBy?: ColumnSort;
  onOrderByChange?: (orderBy: ColumnSort | undefined) => void;
  showPagination?: boolean;
  onNextPageClick?: () => void;
  canGetNextPage?: boolean;
  onPrevPageClick?: () => void;
  canGetPrevPage?: boolean;
  showPageSizeSelect?: boolean;
  maxPageSize?: number;
  pageSize?: number;
  onPageSizeChange?: (pageSize: number) => void;
  rowsCount?: number;
  tableRef?: MutableRefObject<HTMLTableElement>;
  rowsAlignedTopVertically?: boolean;
  enablePinning?: boolean;
  enableRowPinning?: boolean;
  alwaysLeftPinnedColumns?: string[];
  alwaysTopPinnedRows?: string[];
  alwaysBottomPinnedRows?: string[];
  stickyHeader?: boolean;
  sx?: SxProps;
  getRowId?: (row: T) => string;
  onSelectionChange?: (selectedRows: T[]) => void;
  enableMultiSelect?: boolean;
  enableSelectionToolbar?: boolean;
  initialSelectedRows?: T[];
  selectionActions?: Array<{
    label: string;
    onClick: (selectedRows: T[]) => void;
    disabled?: boolean | ((selectedRows: T[]) => boolean);
    divider?: boolean;
  }>;
  showColumnManager?: boolean;
  /**
   * When true, columns expose a drag-handle on their right edge and the
   * user can resize them. Sizing persists per-tableId via the same
   * IndexedDB-backed `useTableSettings` slot as visibility/order.
   */
  enableColumnResizing?: boolean;
  /**
   * When true, table state (column order/visibility/sizing/sorting) is
   * held purely in component memory — IndexedDB is bypassed. Use this
   * when an outer mechanism owns the state (e.g. a saved dashboard view
   * payload) and per-tableId persistence would leak across contexts.
   */
  disablePersistence?: boolean;
  /**
   * Controlled column-state props. When provided they take precedence
   * over the per-tableId IndexedDB cache and become the source of truth.
   * The matching `on*Change` callback fires whenever the user (or table
   * internals) updates that piece. Pass them as a pair to drive the
   * table from outside state (e.g. a saved view).
   */
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>;
  columnOrder?: ColumnOrderState;
  onColumnOrderChange?: OnChangeFn<ColumnOrderState>;
  columnSizing?: ColumnSizingState;
  onColumnSizingChange?: OnChangeFn<ColumnSizingState>;
  /**
   * Column ids that are non-customizable: their visibility checkbox is
   * disabled (forced on) in the column manager, the drag handle is
   * suppressed, and other columns cannot be reordered onto their slot.
   * Typically the entity-identity / hierarchy columns that anchor the
   * table's leftmost pinned strip.
   */
  lockedColumns?: string[];
}

/**
 * Resolve the label shown for a column in the column-manager popover.
 *
 * Precedence:
 *   1. `columnDef.header` — string, ReactNode, or function. Function
 *      headers are invoked with the column's actual header context so
 *      callers can keep using the same JSX they render in the thead
 *      (e.g. `<Stack>Impressions<InfoIcon /></Stack>`).
 *   2. Fallback to `column.id` whenever the header is missing, an empty
 *      string, or — for function/ReactNode headers — resolves to a
 *      falsy/empty value. The row still has to be identifiable, so an
 *      "intentionally blank" header in the thead shouldn't leave the
 *      popover row unnamed.
 */
function renderColumnManagerLabel<T>(column: Column<T, unknown>, table: ReactTable<T>): ReactNode {
  const headerDef = column.columnDef.header;
  if (headerDef == null || headerDef === '') return upcaseFirst(column.id);
  if (typeof headerDef === 'string') return headerDef;
  if (typeof headerDef === 'function') {
    // Prefer the live Header context (some header functions read
    // `column`/`table` from it). Hidden columns are excluded from
    // `getFlatHeaders`, though — for those we synthesize a context so
    // the label survives "Hide all" and other visibility toggles.
    const headerObj = table.getFlatHeaders().find((h) => h.column.id === column.id);
    const context: HeaderContext<T, unknown> = headerObj
      ? headerObj.getContext()
      : ({
          table,
          column,
          header: null as unknown as Header<T, unknown>,
        } as HeaderContext<T, unknown>);
    const rendered = flexRender(headerDef, context);
    if (rendered == null || rendered === false || rendered === '') return column.id;
    return rendered;
  }
  // ReactNode literal (rare for our codebase).
  return headerDef;
}

// Column visibility and ordering manager component
function ColumnVisibilityManager<T>({
  table,
  tableId,
  lockedColumns,
}: {
  table: ReactTable<T>;
  tableId: string;
  lockedColumns: readonly string[];
}) {
  const [compactView, toggleCompactView] = useCompactView(tableId);
  const [open, setOpen] = useState(false);
  const [draggingColumn, setDraggingColumn] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // Columns can opt out of the manager entirely via
  // `meta.hideFromColumnManager`. Useful for action columns (e.g. the
  // row-level "view details" icon) that aren't a user-facing data field
  // — listing them as a toggleable / draggable row would be confusing.
  // The column still renders in the table itself.
  const allColumns = table.getAllLeafColumns().filter((column) => {
    if (column.id === 'select' || !column.getCanHide()) return false;
    const meta = column.columnDef.meta as { hideFromColumnManager?: boolean } | undefined;
    return !meta?.hideFromColumnManager;
  });

  const handleDragStart = (e: React.DragEvent, columnId: string) => {
    setDraggingColumn(columnId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    if (columnId !== draggingColumn) {
      setDragOverColumn(columnId);
    }
  };

  const handleDragEnd = () => {
    setDraggingColumn(null);
    setDragOverColumn(null);
  };

  const handleDrop = (e: React.DragEvent, targetColumnId: string) => {
    e.preventDefault();
    if (!draggingColumn || draggingColumn === targetColumnId) return;
    // Refuse drops onto locked rows: moving the dragged column to a
    // locked slot would shift the locked column out of place. The
    // locked prefix has to stay intact.
    if (lockedColumns.includes(targetColumnId)) {
      setDraggingColumn(null);
      setDragOverColumn(null);
      return;
    }

    const columnOrder = table.getState().columnOrder;
    const currentOrder = columnOrder.length > 0 ? columnOrder : allColumns.map((c) => c.id);

    const dragIndex = currentOrder.indexOf(draggingColumn);
    const targetIndex = currentOrder.indexOf(targetColumnId);

    if (dragIndex !== -1 && targetIndex !== -1) {
      const newOrder = [...currentOrder];
      newOrder.splice(dragIndex, 1);
      newOrder.splice(targetIndex, 0, draggingColumn);
      table.setColumnOrder(newOrder);
    }

    setDraggingColumn(null);
    setDragOverColumn(null);
  };

  return (
    <>
      <Button
        onClick={handleOpen}
        variant="outlined"
        color="secondary"
      >
        Columns
      </Button>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              width: { xs: '100%', sm: 360 },
              maxWidth: '100%',
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
          }}
        >
          <Typography variant="subtitle1">Column Settings</Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            aria-label="Close column settings"
          >
            <Close fontSize="small" />
          </IconButton>
        </Stack>
        <Divider />
        <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              display: 'block',
              mb: 2,
            }}
          >
            Drag items to reorder columns
          </Typography>
          <Stack spacing={1}>
            {allColumns.map((column) => {
              const isLocked = lockedColumns.includes(column.id);
              return (
                <Box
                  key={column.id}
                  draggable={!isLocked}
                  onDragStart={isLocked ? undefined : (e) => handleDragStart(e, column.id)}
                  onDragOver={isLocked ? undefined : (e) => handleDragOver(e, column.id)}
                  onDragEnd={isLocked ? undefined : handleDragEnd}
                  onDrop={isLocked ? undefined : (e) => handleDrop(e, column.id)}
                  sx={{
                    cursor: isLocked ? 'default' : 'move',
                    padding: '4px 8px',
                    borderRadius: 1,
                    backgroundColor:
                      draggingColumn === column.id
                        ? 'action.selected'
                        : dragOverColumn === column.id
                          ? 'action.hover'
                          : 'transparent',
                    transition: 'background-color 0.2s',
                    '&:hover': {
                      backgroundColor: isLocked ? 'transparent' : 'action.hover',
                    },
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={column.getIsVisible()}
                        onChange={column.getToggleVisibilityHandler()}
                        size="small"
                        disabled={isLocked}
                      />
                    }
                    label={
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                          alignItems: 'center',
                        }}
                      >
                        <Box sx={{ color: 'text.secondary', fontSize: '0.8rem', minWidth: 12 }}>
                          {isLocked ? '' : '⋮⋮'}
                        </Box>
                        <Typography
                          variant="body2"
                          component="span"
                        >
                          {renderColumnManagerLabel(column, table)}
                        </Typography>
                      </Stack>
                    }
                  />
                </Box>
              );
            })}
          </Stack>
        </Box>
        <Divider />
        <Stack
          direction="row"
          spacing={1}
          sx={{ px: 2, py: 1.5 }}
        >
          <Button
            size="small"
            color="secondary"
            onClick={() => table.toggleAllColumnsVisible(true)}
          >
            Show All
          </Button>
          <Button
            size="small"
            color="secondary"
            onClick={() => {
              // Hide everything that's not locked; locked columns stay
              // visible because their state isn't a user choice.
              for (const column of allColumns) {
                if (lockedColumns.includes(column.id)) continue;
                column.toggleVisibility(false);
              }
              // The select column (multi-select checkbox column) only
              // exists when enableMultiSelect is on. Look it up via
              // getAllLeafColumns instead of `table.getColumn('select')`
              // so we don't trip TanStack's "Column does not exist"
              // console warning when multi-select is disabled.
              const selectColumn = table.getAllLeafColumns().find((c) => c.id === 'select');
              if (selectColumn) {
                selectColumn.toggleVisibility(true);
              }
            }}
          >
            Hide All
          </Button>
        </Stack>
      </Drawer>
    </>
  );
}

// Stable default `getRowId` — hoisted out of the destructure so it isn't
// re-allocated on every render. An unstable identity here was destabilizing
// the `[data, getRowId]` deps on the row-selection effect, firing it on every
// render and feeding a render storm in `useReactTable`'s internal state.
// biome-ignore lint/suspicious/noExplicitAny: matches the prior signature
const defaultGetRowId = (row: any) => row?.id;
// Stable empty defaults for array props — same rationale as above. Inline
// `[]` defaults in the destructure mean a new array each render, which
// cascades into effects that depend on these arrays.
const EMPTY_ARRAY: readonly any[] = [];

export default function EnhancedTable<T>({
  tableId,
  columns: userColumns,
  data,
  loading,
  error,
  errorState,
  emptyState,
  getRowCanExpand,
  expandedRowRender,
  defaultExpanded,
  expandedRowSpansFull,
  afterEachRowRender,
  showSorting,
  manualSorting,
  defaultOrderBy,
  onOrderByChange,
  showPagination,
  onNextPageClick,
  canGetNextPage,
  onPrevPageClick,
  canGetPrevPage,
  showPageSizeSelect,
  maxPageSize,
  pageSize,
  onPageSizeChange,
  rowsCount,
  tableRef,
  rowsAlignedTopVertically,
  enablePinning,
  enableRowPinning,
  alwaysLeftPinnedColumns,
  alwaysTopPinnedRows,
  alwaysBottomPinnedRows,
  stickyHeader,
  sx,
  getRowId = defaultGetRowId,
  onSelectionChange,
  enableMultiSelect = true,
  enableSelectionToolbar = true,
  initialSelectedRows = EMPTY_ARRAY as T[],
  selectionActions = EMPTY_ARRAY as NonNullable<Props<T>['selectionActions']>,
  showColumnManager = true,
  enableColumnResizing = false,
  disablePersistence = false,
  columnVisibility: controlledColumnVisibility,
  onColumnVisibilityChange: controlledOnColumnVisibilityChange,
  columnOrder: controlledColumnOrder,
  onColumnOrderChange: controlledOnColumnOrderChange,
  columnSizing: controlledColumnSizing,
  onColumnSizingChange: controlledOnColumnSizingChange,
  lockedColumns = EMPTY_ARRAY as string[],
}: Props<T>) {
  const {
    columnOrder: persistedColumnOrder,
    setColumnOrder: persistedSetColumnOrder,
    columnVisibility: persistedColumnVisibility,
    setColumnVisibility: persistedSetColumnVisibility,
    columnSizing: persistedColumnSizing,
    setColumnSizing: persistedSetColumnSizing,
    sorting,
    setSorting,
  } = useTableSettings(tableId, {
    defaultOrderBy,
    manualSorting,
    enabled: !disablePersistence,
  });

  // Each column-state piece can be controlled from outside (when a saved
  // view supplies it) or fall back to the per-tableId persisted state.
  // The state value and the change handler are selected independently:
  // a caller can supply just the state and let the persisted setter run,
  // but typically they're passed as a pair so the table writes to the
  // same store it reads from.
  const columnVisibility = controlledColumnVisibility ?? persistedColumnVisibility;
  const setColumnVisibility: OnChangeFn<VisibilityState> = useCallback(
    (updater: Updater<VisibilityState>) => {
      if (controlledOnColumnVisibilityChange) {
        controlledOnColumnVisibilityChange(updater);
      } else {
        persistedSetColumnVisibility(
          typeof updater === 'function'
            ? (updater as (old: VisibilityState) => VisibilityState)
            : updater,
        );
      }
    },
    [controlledOnColumnVisibilityChange, persistedSetColumnVisibility],
  );

  const columnOrder = controlledColumnOrder ?? persistedColumnOrder;
  const setColumnOrder: OnChangeFn<ColumnOrderState> = useCallback(
    (updater: Updater<ColumnOrderState>) => {
      if (controlledOnColumnOrderChange) {
        controlledOnColumnOrderChange(updater);
      } else {
        persistedSetColumnOrder(
          typeof updater === 'function'
            ? (updater as (old: ColumnOrderState) => ColumnOrderState)
            : updater,
        );
      }
    },
    [controlledOnColumnOrderChange, persistedSetColumnOrder],
  );

  const columnSizing = controlledColumnSizing ?? persistedColumnSizing;
  const setColumnSizing: OnChangeFn<ColumnSizingState> = useCallback(
    (updater: Updater<ColumnSizingState>) => {
      if (controlledOnColumnSizingChange) {
        controlledOnColumnSizingChange(updater);
      } else {
        persistedSetColumnSizing(
          typeof updater === 'function'
            ? (updater as (old: ColumnSizingState) => ColumnSizingState)
            : updater,
        );
      }
    },
    [controlledOnColumnSizingChange, persistedSetColumnSizing],
  );

  const [expanded, setExpanded] = useState<ExpandedState>(defaultExpanded ? true : {});
  // Bump a counter on resize-end so dependent calculations (e.g. column
  // widths derived from header DOM nodes) re-run after the user stops
  // dragging. Mirrors the base Table's pattern.
  const [, setResizeRedrawKey] = useState(0);
  const handleResizeEnd = useCallback(() => {
    setResizeRedrawKey((k) => k + 1);
  }, []);

  // Initialize row selection based on initial selected rows
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(() => {
    const selection: RowSelectionState = {};
    initialSelectedRows.forEach((row) => {
      const rowId = getRowId(row);
      selection[rowId] = true;
    });
    return selection;
  });

  // Add select column if multi-select is enabled
  const columns = useMemo(() => {
    if (!enableMultiSelect) return userColumns;

    const selectColumn: ColumnDef<T> = {
      id: 'select',
      size: 0,
      enableHiding: false,
      enableResizing: false,
      header: ({ table }) =>
        // Only show checkbox in header if selection toolbar is disabled
        !enableSelectionToolbar ? (
          <Checkbox
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ) : null,
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onChange={row.getToggleSelectedHandler()}
          disabled={!row.getCanSelect()}
        />
      ),
    };

    return [selectColumn, ...userColumns];
  }, [userColumns, enableMultiSelect, enableSelectionToolbar]);

  // Create a custom row selection handler that notifies parent
  const handleRowSelectionChange = useCallback(
    (updater: any) => {
      setRowSelection(updater);

      // Only notify parent if there's a selection change handler
      if (onSelectionChange) {
        // Get the new selection state
        const newSelection = typeof updater === 'function' ? updater(rowSelection) : updater;

        // Calculate selected rows based on new selection state
        const selectedRows = Object.keys(newSelection)
          .filter((key) => newSelection[key])
          .map((rowId) => data.find((row) => getRowId(row) === rowId))
          .filter(Boolean) as T[];

        onSelectionChange(selectedRows);
      }
    },
    [rowSelection, onSelectionChange, data, getRowId],
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      expanded,
      rowSelection,
      columnVisibility,
      columnOrder,
      columnSizing,
    },
    getRowId,
    getRowCanExpand,
    manualSorting,
    sortDescFirst: true,
    onSortingChange: setSorting,
    onRowSelectionChange: handleRowSelectionChange,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    enableColumnResizing: enableColumnResizing,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onExpandedChange: setExpanded,
    getSubRows: (row) =>
      Object.prototype.hasOwnProperty.call(row, 'subRows') ? (row as { subRows: any }).subRows : [],
    defaultColumn: {
      size: 1,
      minSize: 0,
    },
    meta: {
      rowsAlignedTopVertically,
    },
    enablePinning: enablePinning,
    enableRowPinning: enableRowPinning,
    enableRowSelection: true,
  });

  // Update selection when data changes to remove stale selections
  useEffect(() => {
    setRowSelection((current) => {
      const validRowIds = new Set(data.map((row) => getRowId(row)));
      const newSelection: RowSelectionState = {};

      // Keep only selections that still exist in the data
      Object.keys(current).forEach((rowId) => {
        if (validRowIds.has(rowId) && current[rowId]) {
          newSelection[rowId] = true;
        }
      });

      // Only update if the selection actually changed
      const hasChanges =
        Object.keys(newSelection).length !== Object.keys(current).length ||
        Object.keys(newSelection).some((key) => !current[key]);

      return hasChanges ? newSelection : current;
    });
  }, [data, getRowId]);

  const { rows } = table.getSortedRowModel();

  const isError = !!error;
  const isEmpty = rows.length === 0;
  const isEdgeState = isError || isEmpty;

  const [maxNumRows, setMaxNumRows] = useState(0);
  useEffect(() => {
    if (rows.length > maxNumRows) {
      setMaxNumRows(rows.length);
    }
  }, [rows.length, maxNumRows]);

  useEffect(() => {
    if (loading || isError || isEmpty) {
      table.setColumnPinning({});
      return;
    }

    table.setColumnPinning({
      left: [...(alwaysLeftPinnedColumns ?? [])],
    });
  }, [alwaysLeftPinnedColumns, loading, isError, isEmpty]); // Removed 'table' from dependencies

  useEffect(() => {
    if (loading || isError || isEmpty) {
      table.setRowPinning({});
      return;
    }

    table.setRowPinning({
      top: alwaysTopPinnedRows,
      bottom: alwaysBottomPinnedRows,
    });
  }, [alwaysTopPinnedRows, alwaysBottomPinnedRows, loading, isError, isEmpty]);

  const dataRef = useRef(data);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const selectedCount = Object.keys(rowSelection).length;

  // Memoize actions to prevent recreation on every render
  const toolbarActions = useMemo(() => {
    // Calculate selected rows based on rowSelection state
    const selectedRows = Object.keys(rowSelection)
      .filter((key) => rowSelection[key])
      .map((rowId) => data.find((row) => getRowId(row) === rowId))
      .filter(Boolean) as T[];

    return selectionActions.map((action) => ({
      ...action,
      onClick: () => action.onClick(selectedRows),
      disabled:
        typeof action.disabled === 'function' ? action.disabled(selectedRows) : action.disabled,
    }));
  }, [selectionActions, rowSelection, data, getRowId]);

  // Prepare selection toolbar config for TableHead
  const selectionToolbarConfig =
    enableMultiSelect && enableSelectionToolbar
      ? {
          selectedCount,
          totalCount: data.length,
          onSelectAll: () => table.toggleAllRowsSelected(true),
          onDeselectAll: () => table.toggleAllRowsSelected(false),
          isAllSelected: table.getIsAllRowsSelected(),
          isSomeSelected: table.getIsSomeRowsSelected(),
          actions: toolbarActions,
        }
      : undefined;

  // ResizeHandle writes per-column `--col-{id}-l` (left-offset) CSS vars
  // during drag to slide subsequent left-pinned columns in lockstep
  // without a React render. The vars never get cleaned up, though — so
  // when the column set changes via visibility (or sizing), React's
  // freshly-computed `leftOffset` is silently overridden by the stale
  // var (look for `var(--col-{id}-l, ${leftOffset}px)` in Table). The
  // result is pinned columns visibly drifting after a Hide All →
  // toggle-one-back, even though the React state is correct.
  //
  // Strategy: keep an internal ref to the <table>, and on every change
  // to visibility / sizing / order, strip every `--col-*-l` var.
  // Subsequent renders fall through to the React-computed fallback,
  // which is recomputed from the current pinned chain.
  const internalTableRef = useRef<HTMLTableElement | null>(null);
  // Mirror to the caller-provided ref, if any, so external consumers
  // still get the DOM element.
  useEffect(() => {
    if (tableRef && internalTableRef.current) {
      tableRef.current = internalTableRef.current;
    }
  });
  useEffect(() => {
    const el = internalTableRef.current;
    if (!el) return;
    const style = el.style;
    // `style.length` and `style.item(i)` iterate over set custom
    // properties — much cheaper than guessing per-column ids.
    for (let i = style.length - 1; i >= 0; i--) {
      const prop = style.item(i);
      if (prop.startsWith('--col-') && prop.endsWith('-l')) {
        style.removeProperty(prop);
      }
    }
  }, [columnVisibility, columnOrder, columnSizing]);

  // Force a full TableComponent remount whenever the visible column set
  // (or its order) changes. Pinned column offsets are computed during
  // render from `trRef.current.querySelector(...).clientWidth`, which
  // reads the PREVIOUS layout's widths — fine when widths happen to
  // match, but after Hide All → toggle-one-back the cached widths
  // belong to a column set that no longer exists, so the sticky
  // positions land on the wrong neighbors and rows visually drift.
  // Bumping a key on these state slices tears down `<TableComponent>`
  // and lets every ref-driven measurement re-init against the
  // freshly-laid-out DOM.
  const repaintKey = useMemo(
    () => `${JSON.stringify(columnVisibility)}|${JSON.stringify(columnOrder)}`,
    [columnVisibility, columnOrder],
  );

  // The "Columns" trigger lives in the footer's start slot; while the
  // selection toolbar is active (rows checked), we hide it to avoid two
  // competing primary actions sharing the footer row.
  const columnManagerSlot =
    showColumnManager && (!enableMultiSelect || !enableSelectionToolbar || selectedCount === 0) ? (
      <ColumnVisibilityManager
        table={table}
        tableId={tableId}
        lockedColumns={lockedColumns}
      />
    ) : null;

  return (
    <TableWrapper sx={sx}>
      <TableComponent
        key={repaintKey}
        hasLeftPinnedColumns={
          Array.isArray(alwaysLeftPinnedColumns) && alwaysLeftPinnedColumns.length > 0
        }
        loading={loading}
        isEdgeState={isEdgeState}
        tableRef={internalTableRef as MutableRefObject<HTMLTableElement>}
      >
        <EnhancedTableHead
          dataRef={dataRef}
          table={table}
          heads={table.getHeaderGroups()}
          showSorting={showSorting}
          onOrderByChange={manualSorting ? onOrderByChange : undefined}
          loading={loading}
          isError={isError}
          isEmpty={isEmpty}
          sticky={stickyHeader}
          selectionToolbar={selectionToolbarConfig}
          enableColumnResizing={enableColumnResizing}
          onResizeEnd={enableColumnResizing ? handleResizeEnd : undefined}
        />
        <tbody>
          <EnhancedTableBody
            dataRef={dataRef}
            table={table}
            cols={table.getAllLeafColumns()}
            rows={rows}
            expandedRowRender={expandedRowRender}
            expandedRowSpansFull={expandedRowSpansFull}
            afterEachRowRender={afterEachRowRender}
            maxNumRows={maxNumRows}
            loading={loading}
            isError={isError}
            errorState={errorState}
            isEmpty={isEmpty}
            emptyState={emptyState}
            bottomPinnedRowIds={alwaysBottomPinnedRows}
          />
        </tbody>
      </TableComponent>
      <TableFooter
        showPagination={showPagination}
        onNextPageClick={onNextPageClick}
        canGetNextPage={canGetNextPage}
        onPrevPageClick={onPrevPageClick}
        canGetPrevPage={canGetPrevPage}
        showPageSizeSelect={showPageSizeSelect}
        maxPageSize={maxPageSize}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
        rowsCount={rowsCount}
        selectedCount={Object.keys(rowSelection).length}
        endSlot={columnManagerSlot}
      />
    </TableWrapper>
  );
}

// Re-export components from the original Table
export {
  EmptyState,
  ErrorState,
  SkeletonRows,
  TableComponent,
  TableHead,
  TableWrapper,
  Tr,
} from '../Table';

// Page size options constant - defined outside component to prevent recreation
const PAGE_SIZES = [10, 25, 50, 100];

// Enhanced TableFooter with selection count
interface EnhancedTableFooterProps {
  showPagination?: boolean;
  maxPageSize?: number;
  onNextPageClick?: () => void;
  canGetNextPage?: boolean;
  onPrevPageClick?: () => void;
  canGetPrevPage?: boolean;
  showPageSizeSelect?: boolean;
  pageSize?: number;
  onPageSizeChange?: (pageSize: number) => void;
  rowsCount?: number;
  selectedCount?: number;
  sx?: SxProps;
  /**
   * Optional element rendered at the left edge of the footer, before
   * the row count. Used for table-level actions that belong with the
   * footer (e.g. the column-manager trigger).
   */
  endSlot?: ReactNode;
}

export function TableFooter({
  showPagination,
  onNextPageClick,
  canGetNextPage,
  onPrevPageClick,
  canGetPrevPage,
  showPageSizeSelect,
  maxPageSize = 100,
  pageSize = 10,
  onPageSizeChange,
  rowsCount,
  selectedCount = 0,
  sx,
  endSlot,
}: EnhancedTableFooterProps) {
  return (
    <Stack
      direction="row"
      sx={{
        gap: 2,
        alignItems: 'center',
        ...sx,
        px: 2,
        mt: 2,
      }}
    >
      {rowsCount && (
        <Box
          sx={{
            flexGrow: 1,
          }}
        >
          <Text size="small">
            {rowsCount} {pluralize(rowsCount, 'result')}
            {selectedCount > 0 && ` (${selectedCount} selected)`}
          </Text>
        </Box>
      )}
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          justifyContent: 'end',
          flexGrow: 1,
          gap: 2,
        }}
      >
        {showPageSizeSelect && (
          <FormControl
            size="small"
            variant="filled"
          >
            <Stack
              direction="row"
              sx={{
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Text size="small">Rows per page</Text>
              <Select
                value={pageSize}
                onChange={(e) => onPageSizeChange?.(Number(e.target.value))}
              >
                {PAGE_SIZES.filter((size) => size <= maxPageSize).map((size) => (
                  <MenuItem
                    key={size}
                    value={size}
                  >
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
          </FormControl>
        )}
        {showPagination && (canGetPrevPage || canGetNextPage) && (
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <IconButton
              onClick={onPrevPageClick}
              disabled={!canGetPrevPage}
              aria-label="Previous page"
            >
              <ChevronLeft />
            </IconButton>
            <IconButton
              onClick={onNextPageClick}
              disabled={!canGetNextPage}
              aria-label="Next page"
            >
              <ChevronRight />
            </IconButton>
          </Stack>
        )}

        {endSlot ? (
          <>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ my: 1 }}
            />
            {endSlot}
          </>
        ) : null}
      </Stack>
    </Stack>
  );
}
